import { makeWordPressAjaxRequest } from "./ajax";
import { documentStore, globalStore, uiStore } from "@/store/store";
import { exportTree } from "../tree/tree";
import * as t from "io-ts";
import { Either, fold, right, left } from "fp-ts/lib/Either";
import { _Error, logErrorsToUiStore } from "./data";
import { pipe } from "fp-ts/lib/function";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { breakdanceUndo } from "@/lib/undo/undo";
import { saveSelectorsToLocalStorage } from "@/oxygen/classes/lib/classes";

export async function saveToWordPressWithErrorsLoggedToUiStore(): Promise<
  Either<_Error[], unknown>
> {

  uiStore.setSaveInProgress(true);

  const dataToPost: Record<string, unknown> = {
    id: documentStore.document.documentMeta.id,
    tree: fpJsonStringify(exportTree(documentStore.document.tree)),
    singularityMeta: fpJsonStringify(
      documentStore.document.documentMeta.singularityMeta
    )
  };

  if (globalStore.settingsHasUnsavedChangesPresent) {
    dataToPost.globalSettings = fpJsonStringify(globalStore.globalSettings);
  }

  if (globalStore.selectorsHasUnsavedChangesPresent) {
    dataToPost.classes = fpJsonStringify(globalStore.cssSelectors);
  }

  if (globalStore.presetsHasUnsavedChangesPresent) {
    dataToPost.presets = fpJsonStringify(globalStore.presets);
  }

  if (globalStore.aiSettingsHasUnsavedChangesPresent) {
    dataToPost.ai = fpJsonStringify(globalStore.aiSettings.prompt);
  }

  if (globalStore.variablesHasUnsavedChangesPresent) {
    dataToPost.variables = fpJsonStringify({
      variables: globalStore.variables,
      collections: globalStore.variablesCollections
    });
  }

  if (globalStore.oxySelectorsHasUnsavedChangesPresent) {
    dataToPost.oxySelectors = fpJsonStringify({
      selectors: globalStore.oxySelectors,
      collections: globalStore.oxySelectorsCollections
    });
  }

  const result = pipe(
    await makeWordPressAjaxRequest({
      friendlyName: "Saving to WordPress",
      action: "breakdance_save",
      dataToPost,
      decoder: t.unknown
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);
        return left(errors);
      },
      r => {
        saveSelectorsToLocalStorage();
        document.dispatchEvent(new Event("breakdancePageSaved"));

        uiStore.setUnsavedChangesPresent(false);
        breakdanceUndo.ignore(() => {
          globalStore.markCurrentGlobalSettingsAsUntouched();
          globalStore.markCurrentSelectorsAsUntouched();
          globalStore.markCurrentVariablesAsUntouched();
          globalStore.markCurrentPresetsAsUntouched();
          globalStore.markCurrentOxySelectorsAsUntouched();
          globalStore.markCurrentAiSettingsAsUntouched();
        });
        return right(r);
      }
    )
  );

  uiStore.setSaveInProgress(false);
  return result;
}
